import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOut, Menu, Settings, Shield, User, X } from 'lucide-react'
import { Avatar, Button, Row, Stack } from '@/components/ui'
import { LoginContext } from '@/lib/auth'

const NAV: Array<[string, string]> = [
  ['/', 'Accueil'],
  ['/events', 'Événements'],
  ['/communautes', 'Communautés'],
  ['/initiatives', 'Initiatives'],
  ['/new-event', 'Proposer'],
  ['/contact', 'Contact'],
]

const menuItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '9px 12px',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-body)',
  fontSize: 14.5,
  fontWeight: 500,
  color: 'var(--ink-1)',
}

export default function SiteHeader() {
  const ctx = useContext(LoginContext)
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [menu, setMenu] = useState(false)
  const [drawer, setDrawer] = useState(false)

  const user = ctx?.user ?? null
  const go = (to: string) => navigate({ to })

  const accountLinks: Array<[string, React.ReactNode, string]> = [
    ['/my-events', <User size={18} />, 'Mes événements'],
    ['/me', <Settings size={18} />, 'Paramètres'],
  ]
  if (user?.admin)
    accountLinks.push(['/admin/events', <Shield size={18} />, 'Espace admin'])

  const isActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname.startsWith(to)

  const navLink = (to: string, label: string) => {
    const on = isActive(to)
    return (
      <a
        key={to}
        href={to}
        onClick={(e) => {
          e.preventDefault()
          go(to)
        }}
        style={{
          color: on ? 'var(--emerald-800)' : 'var(--ink-1)',
          fontSize: 15,
          fontWeight: on ? 700 : 500,
          textDecoration: 'none',
          position: 'relative',
          paddingBottom: 2,
        }}
      >
        {label}
        {on && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: -6,
              height: 2,
              background: 'var(--gold-500)',
              borderRadius: 2,
            }}
          />
        )}
      </a>
    )
  }

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: 'color-mix(in srgb, var(--paper-0) 84%, transparent)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid var(--border-soft)',
      }}
    >
      <Row
        gap="xl"
        style={{
          maxWidth: 'var(--container-max)',
          margin: '0 auto',
          padding: '13px var(--gutter)',
        }}
      >
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault()
            go('/')
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
          }}
        >
          <img src="/assets/oumma-mark.svg" alt="" width="32" height="32" />
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 25,
              color: 'var(--emerald-800)',
              letterSpacing: '-0.02em',
            }}
          >
            Oumma
          </span>
        </a>
        <nav
          className="oumma-nav"
          style={{ display: 'flex', gap: 22, marginLeft: 8 }}
        >
          {NAV.map(([to, label]) => navLink(to, label))}
        </nav>
        <Row gap="md" style={{ marginLeft: 'auto' }}>
          {user ? (
            <div className="oumma-acct" style={{ position: 'relative' }}>
              <button
                onClick={() => setMenu(!menu)}
                aria-label="Mon compte"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 2,
                  borderRadius: 'var(--radius-pill)',
                }}
              >
                <Avatar name={user.email} tone="indigo" size={36} ring />
              </button>
              {menu && (
                <>
                  <div
                    onClick={() => setMenu(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 46,
                      width: 230,
                      background: 'var(--surface-card)',
                      border: '1px solid var(--border-soft)',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: 'var(--shadow-lg)',
                      padding: 8,
                      zIndex: 11,
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 12px 12px',
                        borderBottom: '1px solid var(--rule)',
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--ink-3)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {user.email}
                      </div>
                    </div>
                    {accountLinks.map(([to, ic, lbl]) => (
                      <button
                        key={to}
                        onClick={() => {
                          setMenu(false)
                          go(to)
                        }}
                        style={menuItem}
                      >
                        <span
                          style={{
                            color: 'var(--emerald-700)',
                            display: 'inline-flex',
                          }}
                        >
                          {ic}
                        </span>
                        {lbl}
                      </button>
                    ))}
                    <div
                      style={{
                        borderTop: '1px solid var(--rule)',
                        marginTop: 6,
                        paddingTop: 6,
                      }}
                    >
                      <button
                        onClick={() => {
                          setMenu(false)
                          ctx?.logout()
                        }}
                        style={{ ...menuItem, color: 'var(--ink-2)' }}
                      >
                        <span style={{ display: 'inline-flex' }}>
                          <LogOut size={18} />
                        </span>
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="oumma-acct">
              <Button variant="primary" size="sm" onClick={() => go('/login')}>
                Se connecter
              </Button>
            </div>
          )}
          <button
            className="oumma-burger"
            onClick={() => setDrawer(true)}
            aria-label="Menu"
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              width: 42,
              height: 42,
              background: 'var(--surface-card)',
              border: '1px solid var(--border-soft)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              color: 'var(--ink-1)',
            }}
          >
            <Menu size={22} />
          </button>
        </Row>
      </Row>

      {drawer &&
        createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
            <div
              onClick={() => setDrawer(false)}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(8,30,22,0.45)',
                backdropFilter: 'blur(2px)',
              }}
            />
            <Stack
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 'min(86vw, 320px)',
                background: 'var(--surface-card)',
                boxShadow: 'var(--shadow-lg)',
                animation: 'oummaSlideIn 240ms var(--ease-out) both',
              }}
            >
              <Row
                justify="space-between"
                style={{
                  padding: '16px 18px',
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                <Row gap="sm">
                  <img
                    src="/assets/oumma-mark.svg"
                    alt=""
                    width="26"
                    height="26"
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 700,
                      fontSize: 21,
                      color: 'var(--emerald-800)',
                    }}
                  >
                    Oumma
                  </span>
                </Row>
                <button
                  onClick={() => setDrawer(false)}
                  aria-label="Fermer"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--ink-2)',
                    padding: 4,
                  }}
                >
                  <X size={22} />
                </button>
              </Row>
              <nav style={{ padding: '10px 10px' }}>
                <Stack gap="xs">
                  {NAV.map(([to, label]) => {
                    const on = isActive(to)
                    return (
                      <button
                        key={to}
                        onClick={() => {
                          setDrawer(false)
                          go(to)
                        }}
                        style={{
                          ...menuItem,
                          fontSize: 16,
                          padding: '12px 14px',
                          fontWeight: on ? 700 : 500,
                          color: on ? 'var(--emerald-800)' : 'var(--ink-1)',
                          background: on ? 'var(--surface-tint)' : 'none',
                        }}
                      >
                        {label}
                      </button>
                    )
                  })}
                </Stack>
              </nav>
              <Stack
                gap="xs"
                style={{
                  borderTop: '1px solid var(--rule)',
                  padding: '10px 10px',
                  marginTop: 'auto',
                }}
              >
                {user ? (
                  <>
                    {accountLinks.map(([to, ic, lbl]) => (
                      <button
                        key={to}
                        onClick={() => {
                          setDrawer(false)
                          go(to)
                        }}
                        style={{
                          ...menuItem,
                          fontSize: 15.5,
                          padding: '11px 14px',
                        }}
                      >
                        <span
                          style={{
                            color: 'var(--emerald-700)',
                            display: 'inline-flex',
                          }}
                        >
                          {ic}
                        </span>
                        {lbl}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setDrawer(false)
                        ctx?.logout()
                      }}
                      style={{
                        ...menuItem,
                        fontSize: 15.5,
                        padding: '11px 14px',
                        color: 'var(--ink-2)',
                      }}
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <LogOut size={18} />
                      </span>
                      Se déconnecter
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setDrawer(false)
                      go('/login')
                    }}
                    style={{
                      ...menuItem,
                      fontSize: 15.5,
                      padding: '11px 14px',
                    }}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <User size={18} />
                    </span>
                    Se connecter
                  </button>
                )}
              </Stack>
            </Stack>
          </div>,
          document.body,
        )}
    </header>
  )
}
